import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Eye, EyeOff, CheckSquare, Square, Search, SlidersHorizontal, X, Menu as MenuIcon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { MenuItemCard } from './MenuItemCard';
import { MenuItemModal } from './MenuItemModal';
import { MenuSearch } from './MenuSearch';
import { FilterPanel } from './FilterPanel';
import useMenuStore from '@/lib/stores/menuStore';
import type { MenuItem, MenuSection, MenuItemFormData } from '@/types';
import type { MenuFilterState, SortOptions } from '@/lib/utils/searchUtils';
import { 
  createEmptyFilterState, 
  createDefaultSortOptions, 
  searchAndFilter, 
  countActiveFilters
} from '@/lib/utils/searchUtils';

interface MenuItemListProps {
  section: MenuSection;
  items: MenuItem[];
  sections: MenuSection[];
}

/**
 * MenuItemList - List of menu items within a section
 * Handles item management and bulk operations
 */
export const MenuItemList: React.FC<MenuItemListProps> = ({
  section,
  items,
  sections,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [showSelection, setShowSelection] = useState(false);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<MenuFilterState>(createEmptyFilterState());
  const [sortOptions, setSortOptions] = useState<SortOptions>(createDefaultSortOptions());
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  
  const {
    selectedItems,
    loading,
    error,
    reorderingItems,
    addItem,
    updateItem,
    deleteItem,
    toggleItemAvailability,
    reorderItems,
    toggleItemSelection,
    setSelectedItems,
    clearSelection,
    deleteSelectedItems,
    toggleSelectedItemsAvailability,
  } = useMenuStore();

  // Clear selection when component unmounts (page change)
  useEffect(() => {
    return () => {
      clearSelection();
    };
  }, [clearSelection]);

  // Apply search and filter to section items
  const filteredItems = useMemo(() => {
    const result = searchAndFilter(items, [section], {
      searchQuery,
      filters,
      sort: sortOptions
    });
    return result.items;
  }, [items, section, searchQuery, filters, sortOptions]);

  // Track active filters count
  const activeFiltersCount = countActiveFilters(filters);
  const hasActiveSearchOrFilter = searchQuery.length > 0 || activeFiltersCount > 0;

  // Filter selected items for this section (use filtered items if search/filter is active)
  const currentItems = hasActiveSearchOrFilter ? filteredItems : items;
  const sectionSelectedItems = selectedItems.filter(id => 
    currentItems.some(item => item.id === id)
  );
  const hasSelectedItems = sectionSelectedItems.length > 0;
  const allSectionItemsSelected = currentItems.length > 0 && sectionSelectedItems.length === currentItems.length;

  const handleAddItem = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  // Search and filter handlers
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleFiltersChange = (newFilters: Partial<MenuFilterState>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleSortChange = (newSort: SortOptions) => {
    setSortOptions(newSort);
  };

  const handleClearSearchAndFilters = () => {
    setSearchQuery('');
    setFilters(createEmptyFilterState());
    setSortOptions(createDefaultSortOptions());
  };

  const handleEditItem = (item: MenuItem) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDeleteItem = async (item: MenuItem) => {
    try {
      await deleteItem(item.id);
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const handleToggleAvailability = async (item: MenuItem) => {
    try {
      await toggleItemAvailability(item.id);
    } catch (error) {
      console.error('Error toggling availability:', error);
    }
  };

  const handleToggleSelection = (item: MenuItem) => {
    toggleItemSelection(item.id);
  };

  const handleReorderItems = async (reorderedItems: MenuItem[]) => {
    // Update order property for each item
    const itemsWithUpdatedOrder = reorderedItems.map((item, index) => ({
      ...item,
      order: index
    }));
    
    try {
      await reorderItems(section.id, itemsWithUpdatedOrder);
    } catch (error) {
      console.error('Failed to reorder items:', error);
    }
  };
  const handleSelectAll = () => {
    if (allSectionItemsSelected) {
      // Deselect all current items (filtered or all)
      const otherSelectedItems = selectedItems.filter(id => 
        !currentItems.some(item => item.id === id)
      );
      setSelectedItems(otherSelectedItems);
    } else {
      // Select all current items (filtered or all)
      const allCurrentItemIds = currentItems.map(item => item.id);
      const otherSelectedItems = selectedItems.filter(id => 
        !currentItems.some(item => item.id === id)
      );
      setSelectedItems([...otherSelectedItems, ...allCurrentItemIds]);
    }
  };

  const handleBulkDelete = async () => {
    if (sectionSelectedItems.length === 0) return;
    
    const count = sectionSelectedItems.length;
    if (window.confirm(`Are you sure you want to delete ${count} item${count > 1 ? 's' : ''}? This action cannot be undone.`)) {
      try {
        await deleteSelectedItems();
        setShowSelection(false);
      } catch (error) {
        console.error('Error deleting selected items:', error);
      }
    }
  };

  const handleBulkToggleAvailability = async (available: boolean) => {
    if (sectionSelectedItems.length === 0) return;
    
    try {
      await toggleSelectedItemsAvailability(available);
      setShowSelection(false);
    } catch (error) {
      console.error('Error updating selected items availability:', error);
    }
  };

  const handleModalSubmit = async (data: MenuItemFormData & { imageFile?: File }) => {
    try {
      if (editingItem) {
        // Pass all data for update, including enhanced diet type fields
        const updateData: Partial<MenuItemFormData> & { imageFile?: File } = {
          name: data.name,
          description: data.description || '',
          price: data.price,
          dietType: data.dietType,
          isVegan: data.isVegan || false,
          isJain: data.isJain || false,
          imageFile: data.imageFile,
        };
        
        // Only include optional basic fields if they have values
        if (data.ingredients && data.ingredients.trim()) {
          updateData.ingredients = data.ingredients;
        }
        if (data.allergens && data.allergens.trim()) {
          updateData.allergens = data.allergens;
        }
        if (data.preparationTime && data.preparationTime.trim()) {
          updateData.preparationTime = data.preparationTime;
        }
        if (data.calories && data.calories.trim()) {
          updateData.calories = data.calories;
        }
        if (data.spiceLevel) {
          updateData.spiceLevel = data.spiceLevel;
        }

        // Include enhanced diet type fields
        if (data.enhancedAllergens !== undefined) {
          updateData.enhancedAllergens = data.enhancedAllergens;
        }
        if (data.enhancedSpiceLevel !== undefined) {
          updateData.enhancedSpiceLevel = data.enhancedSpiceLevel;
        }
        if (data.cuisineTags !== undefined) {
          updateData.cuisineTags = data.cuisineTags;
        }
        if (data.customTags !== undefined) {
          updateData.customTags = data.customTags;
        }
        if (data.isSignatureDish !== undefined) {
          updateData.isSignatureDish = data.isSignatureDish;
        }
        if (data.isChefSpecial !== undefined) {
          updateData.isChefSpecial = data.isChefSpecial;
        }

        await updateItem(editingItem.id, updateData);
      } else {
        await addItem({ ...data, sectionId: section.id });
      }
      setIsModalOpen(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error submitting item:', error);
      throw error; // Let the modal handle the error display
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  const toggleSelectionMode = () => {
    if (showSelection) {
      clearSelection();
    }
    setShowSelection(!showSelection);
  };
  return (
    <>
      {/* Main content container - stable regardless of modal state */}
      <div className="">
        {/* Section header with actions - Always visible */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{section.name}</h2>
            <p className="text-sm text-gray-600">
              {loading ? (
                <span className="flex items-center">
                  <MenuIcon className="w-4 h-4 mr-2 text-primary animate-pulse" />
                  Loading items...
                </span>
              ) : hasActiveSearchOrFilter ? (
                <>
                  {filteredItems.length} of {items.length} {items.length === 1 ? 'item' : 'items'}
                  {searchQuery && <span className="ml-1">matching "{searchQuery}"</span>}
                </>
              ) : items.length === 0 ? (
                'No items yet'
              ) : (
                <>
                  {items.length} {items.length === 1 ? 'item' : 'items'}
                </>
              )}
            </p>
          </div>

          <div className="flex items-center space-x-2">
            {/* Bulk selection toggle */}
            {!loading && currentItems.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleSelectionMode}
                leftIcon={showSelection ? <EyeOff className="w-4 h-4" /> : <CheckSquare className="w-4 h-4" />}
              >
                {showSelection ? 'Cancel' : 'Select'}
              </Button>
            )}

            {/* Add item button - Always visible, just disabled when loading */}
            <Button
              variant="primary"
              size="sm"
              onClick={handleAddItem}
              leftIcon={<Plus className="w-4 h-4" />}
              disabled={loading}
            >
              Add Item
            </Button>
          </div>
        </div>

        {/* Search and Filter Bar - Always visible when there are items or when loading */}
        {(items.length > 0 || loading) && (
          <div className="flex items-center space-x-3 mb-4">
            {/* Search Bar */}
            <div className="flex-1">
              <MenuSearch
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={`Search items in ${section.name}...`}
                disabled={loading}
              />
            </div>

            {/* Filter button */}
            <Button
              variant="ghost"
              size="md"
              onClick={() => setIsFilterPanelOpen(true)}
              className={`relative p-2 ${activeFiltersCount > 0 ? 'text-primary' : ''}`}
              disabled={loading}
            >
              <SlidersHorizontal className="w-5 h-5" />
              {activeFiltersCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center">
                  {activeFiltersCount}
                </span>
              )}
            </Button>
          </div>
        )}

        {/* Clear filters button */}
        {hasActiveSearchOrFilter && !loading && (
          <div className="flex justify-end mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearSearchAndFilters}
              leftIcon={<X className="w-4 h-4" />}
              className="text-gray-600"
            >
              Clear all
            </Button>
          </div>
        )}
        {/* Bulk actions bar */}
        <AnimatePresence>
          {showSelection && hasSelectedItems && !loading && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-4"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0 sm:space-x-4">
                {/* Selection info and toggle */}
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleSelectAll}
                      className="flex items-center space-x-2 text-sm text-primary hover:text-primary"
                    >
                      {allSectionItemsSelected ? (
                        <CheckSquare className="w-4 h-4" />
                      ) : (
                        <Square className="w-4 h-4" />
                      )}
                      <span>
                        {allSectionItemsSelected ? 'Deselect All' : 'Select All'}
                      </span>
                    </button>
                    
                    <span className="text-sm text-primary">
                      ({sectionSelectedItems.length} selected{hasActiveSearchOrFilter && ` from ${currentItems.length} filtered`})
                    </span>
                  </div>
                </div>

                {/* Action buttons - responsive layout */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleBulkToggleAvailability(true)}
                    leftIcon={<Eye className="w-4 h-4" />}
                    disabled={loading}
                    className="flex-1 sm:flex-none min-w-0"
                  >
                    <span className="hidden sm:inline">Make Available</span>
                    <span className="sm:hidden">Available</span>
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleBulkToggleAvailability(false)}
                    leftIcon={<EyeOff className="w-4 h-4" />}
                    disabled={loading}
                    className="flex-1 sm:flex-none min-w-0"
                  >
                    <span className="hidden sm:inline">Make Unavailable</span>
                    <span className="sm:hidden">Unavailable</span>
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBulkDelete}
                    leftIcon={<Trash2 className="w-4 h-4" />}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-1 sm:flex-none min-w-0"
                    disabled={loading}
                  >
                    <span className="hidden sm:inline">Delete Selected</span>
                    <span className="sm:hidden">Delete</span>
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error display */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
        {/* Items grid */}
        {loading ? (
          <div className="text-center py-8 sm:py-12">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 animate-pulse">
              <MenuIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <p className="text-gray-600 text-sm sm:text-base">Loading items...</p>
          </div>
        ) : items.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300"
          >
            <div className="mx-auto w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mb-4">
              <Plus className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No items yet</h3>
            <p className="text-gray-600 mb-6">
              Start building your menu by adding your first item to this section.
            </p>
            <Button
              variant="primary"
              onClick={handleAddItem}
              leftIcon={<Plus className="w-4 h-4" />}
              disabled={loading}
            >
              Add First Item
            </Button>
          </motion.div>
        ) : filteredItems.length === 0 && hasActiveSearchOrFilter ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200"
          >
            <div className="mx-auto w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mb-4">
              <Search className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No matching items</h3>
            <p className="text-gray-600 mb-6">
              Try adjusting your search terms or filters to find items.
            </p>
            <Button
              variant="ghost"
              onClick={handleClearSearchAndFilters}
              leftIcon={<X className="w-4 h-4" />}
            >
              Clear search and filters
            </Button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Show items based on search/filter state */}
            {hasActiveSearchOrFilter ? (
              // When searching/filtering, show filtered items without reorder controls
              filteredItems.map((item) => (
                <MenuItemCard
                  key={item.id}
                  item={item}
                  isSelected={selectedItems.includes(item.id)}
                  onEdit={handleEditItem}
                  onDelete={handleDeleteItem}
                  onToggleAvailability={handleToggleAvailability}
                  onToggleSelection={handleToggleSelection}
                  showSelection={showSelection}
                />
              ))
            ) : (
              // When showing all items, enable reorder controls
              items.map((item) => (
                <MenuItemCard
                  key={item.id}
                  item={item}
                  isSelected={selectedItems.includes(item.id)}
                  onEdit={handleEditItem}
                  onDelete={handleDeleteItem}
                  onToggleAvailability={handleToggleAvailability}
                  onToggleSelection={handleToggleSelection}
                  showSelection={showSelection}
                  allItems={items}
                  onReorderItems={handleReorderItems}
                  isReordering={reorderingItems.includes(item.id)}
                  isAnyReordering={reorderingItems.length > 0}
                />
              ))
            )}
          </div>
        )}

        {/* Modal */}
        <MenuItemModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
          sections={sections}
          item={editingItem}
          defaultSectionId={section.id}
        />
      </div>
      
      {/* Filter Panel - Outside main container to avoid spacing issues */}
      <FilterPanel
        isOpen={isFilterPanelOpen}
        onClose={() => setIsFilterPanelOpen(false)}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        sort={sortOptions}
        onSortChange={handleSortChange}
        items={items}
        sections={[section]}
      />
    </>
  );
};